import clsx from 'clsx'
import Image from 'next/image'

import Link from '@/atoms/Link/Link'

/* assets */
import { ReactComponent as ChevronRightIcon } from 'public/assets/icons/fi:chevron-right.svg'

interface Props {
  title: string
  image?: StrapiImageFile
  class?: string
  caseStudies: {
    slug: string
    customerLogoWhite: StrapiImageFile
    caseStudySquareCardBgImage: StrapiImageFile
    statisticsList: StrapiBlockComponent[]
  }[]
}

function CaseStudies({ title, image, caseStudies, ...props }: Props) {
  const firstRowCaseStudies = caseStudies.slice(0, caseStudies.length / 2)
  const secondRowCaseStudies = caseStudies.slice(caseStudies.length / 2)

  return (
    <section>
      <div className="container | py-10 md:py-18">
        {/* Header */}
        <div
          className={clsx(
            'flex items-center gap-60 | mb-14 md:mb-10',
            props.class
          )}
        >
          <h2
            className="text-3xl md:text-5xl font-medium | md:max-w-col-8 mb-6"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          {image?.url ? (
            <Image
              src={image.url}
              alt={image.alternativeText ?? ''}
              width={image.width}
              height={image.height}
              className="hidden md:block"
            />
          ) : null}
        </div>

        {/* First Row */}
        <div className="flex justify-center xl:justify-start flex-wrap xl:flex-nowrap gap-5 | mx-auto mb-5">
          <div className="flex flex-wrap md:flex-nowrap gap-6 items-center bg-blue-40 | rounded-xl max-w-xs md:max-w-2xl px-5 py-9 md:p-7">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold | mb-4">
                Cutting through the clutter
              </h2>
              <p className="text-lg">
                with high-quality content, at speed and scale
              </p>
            </div>
            <Image
              src="/assets/images/case-study-largecard-img-2.png"
              alt=""
              width={261}
              height={206}
            />
          </div>
          {firstRowCaseStudies.map((item, index) => (
            <div key={item.slug} className="relative rounded-xl">
              <Image
                src={item.caseStudySquareCardBgImage.url}
                alt={item.caseStudySquareCardBgImage.alternativeText ?? ''}
                width={item.caseStudySquareCardBgImage.width}
                height={item.caseStudySquareCardBgImage.height}
                className="relative | object-cover rounded-xl md:max-w-[293px]"
              />
              <Link
                key={item.slug}
                href={`/case-study/${item.slug}`}
                className="absolute flex flex-col top-0 w-full h-full p-6"
              >
                <div className="h-16">
                  <Image
                    src={item.customerLogoWhite.url}
                    alt={item.customerLogoWhite.alternativeText ?? ''}
                    width={item.customerLogoWhite.width}
                    height={item.customerLogoWhite.height}
                    className="w-18"
                  />
                </div>
                <h2
                  className="text-4xl font-semibold | mb-4"
                  dangerouslySetInnerHTML={{
                    __html: item.statisticsList[index].title ?? ''
                  }}
                />
                <span
                  className="line-clamp-2 h-12 | mb-5"
                  dangerouslySetInnerHTML={{
                    __html: item.statisticsList[index].description ?? ''
                  }}
                />

                <span className="text-lg text-blue-web-90 font-semibold |  flex items-center gap-2  |  mt-auto">
                  Read Case Study
                  <span className="text-white bg-blue-web-90 p-2 rounded-full">
                    <ChevronRightIcon className="font-semibold" />
                  </span>
                </span>
              </Link>
            </div>
          ))}
        </div>

        {/* Second Row */}
        <div className="flex justify-center xl:justify-start flex-wrap xl:flex-nowrap gap-5 | mx-auto mb-5">
          {secondRowCaseStudies.map((item, index) => (
            <div key={item.slug} className="relative rounded-xl">
              <Image
                src={item.caseStudySquareCardBgImage.url}
                alt={item.caseStudySquareCardBgImage.alternativeText ?? ''}
                width={item.caseStudySquareCardBgImage.width}
                height={item.caseStudySquareCardBgImage.height}
                className="relative | rounded-xl md:max-w-[293px]"
              />
              <Link
                key={item.slug}
                href={`/case-study/${item.slug}`}
                className="absolute flex flex-col top-0 w-full h-full p-6"
              >
                <div className="h-16">
                  <Image
                    src={item.customerLogoWhite.url}
                    alt={item.customerLogoWhite.alternativeText ?? ''}
                    width={item.customerLogoWhite.width}
                    height={item.customerLogoWhite.height}
                    className="w-18"
                  />
                </div>
                <h2
                  className="text-4xl font-semibold | mb-4"
                  dangerouslySetInnerHTML={{
                    __html: item.statisticsList[index].title ?? ''
                  }}
                />
                <span
                  className="line-clamp-2 h-12 | mb-5"
                  dangerouslySetInnerHTML={{
                    __html: item.statisticsList[index].description ?? ''
                  }}
                />

                <span className="text-lg text-blue-web-90 font-semibold |  flex items-center gap-2  |  mt-auto">
                  Read Case Study
                  <span className="text-white bg-blue-web-90 p-2 rounded-full">
                    <ChevronRightIcon className="font-semibold" />
                  </span>
                </span>
              </Link>
            </div>
          ))}
          <div className="flex flex-wrap md:flex-nowrap gap-6 items-center bg-blue-40 | rounded-xl max-w-xs md:max-w-2xl px-5 py-9 md:p-7">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold | mb-4">
                Accelerate revenue growth
              </h2>
              <p className="text-lg">
                with content that drives engagement and conversions
              </p>
            </div>
            <Image
              src="/assets/images/case-study-largecard-img-2.png"
              alt=""
              width={261}
              height={206}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default CaseStudies
